import styled from 'styled-components';
import { Row } from '@widgets/GeoSchemes';

export const Viewport = styled.div`
  position: relative;
  overflow: auto;
  max-height: 560px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.surface};
`;

export const Canvas = styled.div<{ $zoom: number }>`
  position: relative;
  width: ${({ $zoom }) => `${$zoom * 100}%`};

  img {
    display: block;
    width: 100%;
    height: auto;
    user-select: none;
    -webkit-user-drag: none;
  }
`;

export const Overlay = styled.svg<{ $editable: boolean }>`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  cursor: ${({ $editable }) => ($editable ? 'crosshair' : 'default')};
  touch-action: ${({ $editable }) => ($editable ? 'none' : 'auto')};

  /* Точки графики листа — приглушённо; с Alt (без прилипания) — ещё тише. */
  .snaps {
    pointer-events: none;
    opacity: 0.75;
    transition: opacity ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};
  }
  .snaps.free {
    opacity: 0.3;
  }
  .snap-halo,
  .hot-halo {
    fill: none;
    stroke: ${({ theme }) => theme.colors.backgroundElevated};
    stroke-linecap: round;
    vector-effect: non-scaling-stroke;
  }
  .snap-halo {
    stroke-width: 3;
  }
  .snap-line {
    fill: none;
    stroke: ${({ theme }) => theme.colors.status.info};
    stroke-width: 1.25;
    stroke-linecap: round;
    vector-effect: non-scaling-stroke;
  }
  .snap-dot {
    fill: ${({ theme }) => theme.colors.status.info};
    stroke: ${({ theme }) => theme.colors.backgroundElevated};
    stroke-width: 1.5;
    paint-order: stroke;
    vector-effect: non-scaling-stroke;
  }

  /* Ближайшая под курсором — ярко, цветом действия. */
  .hot {
    pointer-events: none;
  }
  .hot-ring {
    fill: ${({ theme }) => theme.colors.primarySoft};
    stroke: ${({ theme }) => theme.colors.primary};
    stroke-width: 1.5;
    vector-effect: non-scaling-stroke;
  }
  .hot-halo {
    stroke-width: 4.5;
  }
  .hot-line {
    fill: none;
    stroke: ${({ theme }) => theme.colors.primary};
    stroke-width: 2;
    stroke-linecap: round;
    vector-effect: non-scaling-stroke;
  }
  .hot-dot {
    fill: ${({ theme }) => theme.colors.primary};
    stroke: ${({ theme }) => theme.colors.backgroundElevated};
    stroke-width: 2;
    paint-order: stroke;
    vector-effect: non-scaling-stroke;
  }
  .hot-label {
    fill: ${({ theme }) => theme.colors.primary};
    font-family: ${({ theme }) => theme.font.family};
    font-weight: 600;
    paint-order: stroke;
    stroke: ${({ theme }) => theme.colors.backgroundElevated};
    stroke-width: 3;
    vector-effect: non-scaling-stroke;
  }
`;

export const Marker = styled.g<{ $selected: boolean }>`
  cursor: grab;
  outline: none;

  .ring,
  .cross {
    fill: none;
    stroke: ${({ theme, $selected }) => ($selected ? theme.colors.primary : theme.colors.status.info)};
    vector-effect: non-scaling-stroke;
  }
  .ring {
    stroke-width: 2;
    fill: ${({ theme }) => theme.colors.backgroundElevated}66;
  }
  .cross {
    stroke-width: 1.25;
  }
  .label {
    fill: ${({ theme, $selected }) => ($selected ? theme.colors.primary : theme.colors.status.info)};
    font-family: ${({ theme }) => theme.font.mono};
    font-weight: 700;
    paint-order: stroke;
    stroke: ${({ theme }) => theme.colors.backgroundElevated};
    stroke-width: 3;
    vector-effect: non-scaling-stroke;
  }
  .focus {
    fill: none;
    stroke: ${({ theme }) => theme.colors.primary};
    stroke-width: 2;
    stroke-dasharray: 4 3;
    vector-effect: non-scaling-stroke;
    visibility: hidden;
  }
  &:focus-visible .focus {
    visibility: visible;
  }
`;

export const Toolbar = styled(Row)`
  margin-bottom: ${({ theme }) => theme.spacing(2)};
`;

export const ZoomValue = styled.span`
  min-width: 48px;
  text-align: center;
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 12.5px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

/** Строка состояния под листом, как в CAD: что поставлено и что под курсором. */
export const Readout = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  gap: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(4)}`};
  min-height: 36px;
  margin-top: ${({ theme }) => theme.spacing(2)};
  padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(3)}`};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.surface};
  font-size: 12.5px;
  line-height: 1.45;
`;

export const PickText = styled.div`
  min-width: 0;
  color: ${({ theme }) => theme.colors.text.primary};
  overflow-wrap: anywhere;
  text-wrap: pretty;

  b {
    font-family: ${({ theme }) => theme.font.mono};
    font-weight: 700;
  }
`;

/** Точность: зелёная метка у точки с графики, текст — обычным цветом (контраст AA). */
export const Accuracy = styled.span<{ $exact: boolean }>`
  white-space: nowrap;
  color: ${({ theme }) => theme.colors.text.secondary};

  &::before {
    content: '';
    display: inline-block;
    width: 7px;
    height: 7px;
    margin-right: ${({ theme }) => theme.spacing(1.5)};
    border-radius: 50%;
    vertical-align: 1px;
    background: ${({ theme, $exact }) =>
      $exact ? theme.colors.status.success : theme.colors.borderStrong};
  }
`;

export const CursorText = styled.div`
  font-family: ${({ theme }) => theme.font.mono};
  font-variant-numeric: tabular-nums;
  color: ${({ theme }) => theme.colors.text.secondary};
  white-space: nowrap;
`;

export const Legend = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(4)}`};
  margin-top: ${({ theme }) => theme.spacing(2)};
  font-size: 12px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const LegendItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1.5)};
  white-space: nowrap;

  svg {
    flex-shrink: 0;
  }
  .g {
    fill: none;
    stroke: ${({ theme }) => theme.colors.status.info};
    stroke-width: 1.25;
    stroke-linecap: round;
  }
  .d {
    fill: ${({ theme }) => theme.colors.status.info};
  }
`;

export const Kbd = styled.kbd`
  display: inline-block;
  padding: 0 ${({ theme }) => theme.spacing(1.5)};
  border: 1px solid ${({ theme }) => theme.colors.borderStrong};
  border-bottom-width: 2px;
  border-radius: ${({ theme }) => theme.radius.xs};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  color: ${({ theme }) => theme.colors.text.primary};
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 11.5px;
  line-height: 1.5;
`;

export const SnapNotes = styled.ul`
  margin: ${({ theme }) => `${theme.spacing(1.5)} 0 0`};
  padding-left: ${({ theme }) => theme.spacing(4)};
  font-size: 12px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text.secondary};
`;
